import { useState, useEffect, useCallback } from 'react';

export type FormViewportPosition = 'above' | 'below' | 'inView';

interface UseFormScrollPositionProps {
  targetRef: React.RefObject<HTMLElement | null>;
}

export function useFormScrollPosition({ targetRef }: UseFormScrollPositionProps) {
  const [formPosition, setFormPosition] = useState<FormViewportPosition>('inView');

  const updatePosition = useCallback(() => {
    const target = targetRef.current;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    if (rect.bottom < 0) {
      setFormPosition('above');
    } else if (rect.top > windowHeight) {
      setFormPosition('below');
    } else {
      setFormPosition('inView');
    }
  }, [targetRef]);

  useEffect(() => {
    window.addEventListener('scroll', updatePosition, { passive: true });
    updatePosition();

    return () => {
      window.removeEventListener('scroll', updatePosition);
    };
  }, [updatePosition]);

  const scrollToTarget = useCallback(() => {
    targetRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [targetRef]);

  return {
    formPosition,
    scrollToTarget,
  };
}
